import React, { Fragment, useContext, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import CheckIcon from "@mui/icons-material/Check"
import ClearIcon from "@mui/icons-material/Clear"
import { CircularProgress, Grid, Skeleton } from "@mui/material"
import { RadioButton, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormikContext } from "formik"

import { KonanPagination } from "../../../components/tables/KonanPagination"
import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { DataFileGroup } from "../../../types/generated/api/DataFileGroup"
import type { PaginatedDataFileGroupListList } from "../../../types/generated/api/PaginatedDataFileGroupListList"
import { type SchemaFeature, WorkflowCanvasContext } from "../../Workflows"
import type { WorkflowSimulationFormik } from "../../Workflows/types/interfaces"
import type { ExistingDatasetViewProps } from "../Interfaces"

import styles from "./DataSource.module.scss"

const DataFileItem = (
  props: Readonly<{ name: string; id: string; setDataFile: (uuid: string, columns: string[]) => void }>,
): React.ReactElement => {
  const { name, id, setDataFile } = props
  const { id: projectID } = useParams<{ id: string }>()

  const { values, setFieldValue } = useFormikContext<WorkflowSimulationFormik>()
  const { workflow } = useContext(WorkflowCanvasContext)

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationCreationDialog.existingDatasetView.dataFileItem",
  })

  const theme = getTheme()

  // Load data files
  const { isLoading: isDataFileLoading, data: dataFile } = useQuery<
    AxiosResponse<DataFileGroup>,
    AxiosError,
    DataFileGroup & { isValid: boolean }
  >(["data-file", id], () => KonanAPI.fetchDataFile(projectID as string, id), {
    select: ({ data }) => {
      return {
        ...data,
        isValid: (workflow?.active_version?.schema?.features as SchemaFeature[])
          ?.filter((feat) => feat?.is_required)
          ?.every((el) => data.active_version.columns?.includes(el?.name)),
      }
    },
  })

  return (
    <Grid container item xs={12} className={styles.existingDataSetRow}>
      <Grid item xs={6}>
        <RadioButton
          value={id}
          checked={id === values.predictions_datafile}
          onChange={(e) => {
            setDataFile(e.target.value, dataFile?.active_version.columns ?? [])

            setFieldValue("shouldProceed", true)
          }}
          label={name}
          id={id}
        />
      </Grid>

      <Grid container item xs={6}>
        {name.endsWith(".json") ? (
          <Typography variant="p" variantColor={2}>
            {t("noSchemaInference")}
          </Typography>
        ) : (
          <Fragment>
            <Grid item mr={1} pt={0.5}>
              {dataFile?.isValid ? (
                <CheckIcon fontSize="small" style={{ color: theme.palette.green.text[2] }} />
              ) : isDataFileLoading ? (
                <CircularProgress size={20} style={{ color: "var(--blue-text-2)" }} />
              ) : (
                <ClearIcon fontSize="small" style={{ color: theme.palette.red.text[2] }} />
              )}
            </Grid>
            <Grid item alignSelf={"center"}>
              <Typography variant="p" variantColor={2}>
                {dataFile?.isValid
                  ? t("status.valid")
                  : isDataFileLoading
                    ? t("status.validating")
                    : t("status.invalid")}
              </Typography>
            </Grid>
          </Fragment>
        )}
      </Grid>
    </Grid>
  )
}

/**
 * Simulation existing dataset selection screen
 *
 * @param {WorkflowVersionsRetrieve} workflow
 * @param {(uuid: string, columns: string[]) => void} setDataFile
 * @return {React.ReactElement}
 */
export function ExistingDatasetView(props: Readonly<ExistingDatasetViewProps>): React.ReactElement {
  const { setDataFile } = props

  const { id: projectID } = useParams<{ id: string }>()

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationCreationDialog.existingDatasetView",
  })

  const pageSize = 7
  const [page, setPage] = useState<number>(0)

  // Load data files
  const { isLoading: isDataFilesLoading, data: dataFiles } = useQuery<
    AxiosResponse<PaginatedDataFileGroupListList>,
    AxiosError
  >(["predictions-data-files", projectID, page, pageSize], () =>
    KonanAPI.fetchDataFiles({ project_uuid: projectID as string, type: ["PREDICTIONS"], pageSize, page: page + 1 }),
  )

  return (
    <Grid container>
      {!isDataFilesLoading && dataFiles?.data?.count && dataFiles?.data?.count < 1 ? (
        // TODO:: refactor margin to be calculated dynamically
        <Grid item xs={12} mt={15}>
          <KonanEmptyState title={t("noDatasetsTitle")} />
        </Grid>
      ) : (
        <Fragment>
          {/** dataset table */}
          <Grid item xs={12} mb={1}>
            <Typography variant="label-bold" variantColor={2}>
              {t("title")}
            </Typography>
          </Grid>

          <Grid container item xs={12} className={styles.existingDataSetTable} padding={0}>
            {isDataFilesLoading ? (
              [1, 2, 3, 4].map((_item, index) => (
                <Grid container item xs={12} className={styles.existingDataSetRow} key={index}>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" width="75%" height="20%" />
                  </Grid>

                  <Grid item xs={6}>
                    <Skeleton animation="wave" width="75%" height="20%" />
                  </Grid>
                </Grid>
              ))
            ) : (
              <Grid item container xs={12} height={"340px"} overflow={"hidden"} justifyContent={"space-between"}>
                <Grid item container xs={12} borderBottom={"1px solid var(--base-border-1)"}>
                  {dataFiles?.data.results?.map((item) => (
                    <DataFileItem key={item.uuid} name={item.name} id={item.uuid} setDataFile={setDataFile} />
                  ))}
                </Grid>

                <Grid item xs={12} alignSelf={"flex-end"} className={styles.pagination}>
                  <KonanPagination
                    count={dataFiles?.data.count}
                    page={page}
                    onPageChange={setPage}
                    rowsPerPage={pageSize}
                    hideRowCount={true}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Fragment>
      )}
    </Grid>
  )
}
